import { People } from '@mui/icons-material'
import { Button, Grid, Paper, TextField } from '@mui/material'
import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import RoleContext from '../auth/RoleContext'
import { getRequestConfig, getToken } from '../auth/auth'
import FormViewCommandBar from '../commandBar/FormViewCommandBar'
import { ApiGateway } from '../config/config'
import ConfirmDialog from '../form/ConfirmDialog'
import Page from '../layout/Page'
import UploadFileControl from './UploadFileControl'
import UserCard from './UserCard'
import eventBus from './eventBus';
import appBus from '../utils/EventBus.js';
import Event from '../utils/EventNames.js';
import { getOwnershipList } from '../ownership/data.js'
import { findGroups } from './data/membership.js'

export default function UserFormPage(props = {}) {
  const searchFilter = props.searchFilter
  const params = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [saveClicked, setSaveClicked] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState(false)
  const [tryAgainDialog, setTryAgainDialog] = useState(false)
  const [initialized, setInitialized] = useState(false)
  // const [isNewUser, setIsNewUser] = useState(params.koId === 'new')
  const [directoryUserFound, setDirectoryUserFound] = useState(false)
  const [directoryUserNotFound, setDirectoryUserNotFound] = useState(false)
  const [appUserFound, setAppUserFound] = useState(false)
  const [users, setUsers] = useState([])
  const [email, setEmail] = useState('');
  const [errorsText, setErrorsText] = useState('');
  const [errorsDialogOpen, setErrorsDialogOpen] = useState(false);
  const [usersNotFoundDialogOpen,setUsersNotFoundDialogOpen] = useState(false)
  const [missingUsers, setMissingUsers] = useState([])
  const [noUsersFoundDialogOpen, setNoUsersFoundDialogOpen] = useState(false)
  const [groupConfig, setGroupConfig] = useState({ roleGroupMappings: [], groupIdentities: [] })

  const isNewUser = () => {
    return params.koId === 'new'
  }
  const isMultiEdit = () => {
    return params.koId === 'selected'
  }

  const getUserByKoId = async (koId, requestConfig) => {
    if (!requestConfig) {
      requestConfig = await getRequestConfig()
    }
    const basePath = `${ApiGateway.profile}/${koId}`
    startLoading()
    try {
      const response = await axios.get(basePath, requestConfig)
      if (response.data) {
        const u = response.data
        const _u = { 
          ...u, 
          koId, 
          role: u.roles[0], 
          manufacturerUuids: u.manufacturerUuids ?? [], 
          mfrProfileIds: u.mfrProfileIds ?? [], 
          ownershipIds: u.ownershipIds ?? [] 
        }
        // const _users = [...users, _u]
        // setUsers(_users)
        return _u
      } else {
        setAppUserFound(false)
      }
    } catch (error) {
      setAppUserFound(false)
    } finally {
      stopLoading()
    }
  }

  const handleSetUsers = useCallback((_users) => {
    setUsers([..._users])
    const modifiedUsers = (_users ?? []).filter(u => !!u.modified)
    setIsDirty(modifiedUsers.length > 0)
  }, [])

  const saveUsers = async () => {
    const updatedUsers = users.filter(u => !!u.modified)
    const unmodifiedUsers = users.filter(u => [undefined, false].includes(u.modified))
    updatedUsers.forEach(u => {
      eventBus.emit(`${u.email}-loading`, true)
    })

    const requestConfig = await getRequestConfig()

    const failedUsers = [], successfulUsers = []

    for (const u of updatedUsers) {
      const basePath = `${ApiGateway.profile}/${u.koId}`
      let newU
      try {
        const _user = { ...u, roles: [u.role] }
        await axios.post(basePath, _user, requestConfig)
        successfulUsers.push({ ...u, modified: false, newUser: false, status: 'success' })
      } catch (error) {
        failedUsers.push({ ...u, status: 'error', errors: [{ prop: 'save', message: error.message }]})
      }
    }

    updatedUsers.forEach(u => {
      eventBus.emit(`${u.email}-loading`, false)
    })
    let _users = []
    if ((isNewUser())) {
      _users = [...failedUsers, ...unmodifiedUsers]
    } else {
      _users = [...successfulUsers, ...failedUsers, ...unmodifiedUsers]
    }
    handleSetUsers(_users)
    return _users
  }

  const saveUser = async () => {

    const updatedUsers = users.filter(u => !!u.modified)

    const promises = updatedUsers.map(u => {
      return new Promise(async (f, r) => {
        const basePath = `${ApiGateway.profile}/${u.koId}`
        eventBus.emit(`${u.email}-loading`, true)

        try {
          const requestConfig = await getRequestConfig()
          const _user = { ...u, roles: [u.role] }
          await axios.post(basePath, _user, requestConfig)
          f(u.email)
        } catch (error) {
          r({ email: u.email, error: error.message })
        }
      })
    })

    const results = await Promise.allSettled(promises)
    const failed = results.filter(r => r.status === 'rejected').map(r => r.reason)
    const failedEmails = failed.map(f => f.email)
    const failedUsers = users.filter(u => failedEmails.includes(u.email))
      .map(u => {
        const error = failed.find(f => f.email === u.email)
        eventBus.emit(`${u.email}-loading`, false)
        return { ...u, errors: [{ prop: 'save', message: error.error }] }
      })
    const successfulUsers = users.filter(u => !failedEmails.includes(u.email))
    if(isNewUser()) {
      handleSetUsers(failedUsers)
    } else {
      successfulUsers.forEach(u => {
        u.modified = false
        eventBus.emit(`${u.email}-loading`, false)
      })
      handleSetUsers([...successfulUsers, ...failedUsers])
    }
  }

  const onClear = useCallback((email) => {
    const _users = users.filter((u) => u.email !== email)
    handleSetUsers(_users)
  }, [handleSetUsers, users])

  const onDelete = useCallback(async (u) => {
    const requestConfig = await getRequestConfig()
    const basePath = `${ApiGateway.profile}/${u.koId}`
    try {
      await axios.delete(basePath, requestConfig)
      const _users = users.filter((_u) => _u.email !== u.email)
      handleSetUsers(_users)
    } catch (error) {
      console.error(error)
    } finally {
      return true
    }
  }, [handleSetUsers, users])

  let saveUnsuccessfulError
  const getSaveUnsuccessfulText = () => {
    return `Could not save user due to ${saveUnsuccessfulError}, do you want to try again?`
  }

  const hideTryAgainDialog = () => {
    setTryAgainDialog(false)
  }

  const showDialog = () => {
    setConfirmDialog(true)
  }
  const hideDialog = () => {
    setConfirmDialog(false)
  }

  const onSave = () => {
    setSaveClicked(true)
    setTryAgainDialog(false)
    saveUsers().then((_users) => {
      setDirty(false, _users)
      setSaveClicked(false)
    })
  }

  const confirmCancel = () => {
    if (isDirty) { showDialog() }
    else { handleClose() }
  }

  const handleClose = () => {
    if (location.key === 'default') {
      navigate('/users/list')
    } else {
      navigate(-1)
    }
  }

  const startLoading = useCallback(() => {
    setIsLoading(true)
  }, [])

  const stopLoading = useCallback(() => {
    setIsLoading(false)
  }, [])

  function setDirty(dirty = true, _users) {
    const modifiedUsers = (_users ?? users).filter(u => !!u.modified)
    setIsDirty(dirty || modifiedUsers.length > 0)
  }

  const getDirectoryUser = async (search, filterBy) => {
    startLoading()

    try {
      const tokenResponse = await getToken()
      if (tokenResponse) {
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenResponse.accessToken}`
        }
        const graphUrl = [
          'https://graph.microsoft.com/v1.0/users?',
          `$filter=${filterBy} eq '${search}'`,
          '&',
          '$select=',
          ['mail', 'givenName', 'surname', 'displayName', 'onPremisesSamAccountName'].join(',')
        ].join('')
        const searchResponse = await axios.get(graphUrl, { headers: headers })
        const _user = searchResponse.data?.value.length ? searchResponse.data?.value[0] : undefined
  
        if (_user && _user.onPremisesSamAccountName !== null) {
          setDirectoryUserFound(true)
          const u = await getUserByKoId(_user.onPremisesSamAccountName)
          console.log(u)
          if (u) {
            handleSetUsers([...users, {...u, newUser: false, notify: false}])
            setEmail('')
          } else {
            handleSetUsers([...users, {
              email: _user.mail,
              firstName: _user.givenName,
              lastName: _user.surname,
              koId: _user.onPremisesSamAccountName,
              manufacturerUuids: [],
              mfrProfileIds: [],
              ownershipIds: [],
              valid: true,
              errors: [],
              newUser: true,
              notify: true
            }])
            setEmail('')
          }
        } else {
          setDirectoryUserNotFound(true)
        }
      }
    } catch(err) {
      appBus.emit(Event.ALERT, {
        "text": 'Failed to lookup user in the directory',
        "style": "error",
        "duration": 2000,
      })
    } finally {
      stopLoading()
    }
  }

  const handleLookupClick = () => {
    const _users = [...users]
    const existing = _users.find((u) => u.email === email)
    if (!email) {
      alert('Please enter an email')
    } else if (existing) {
      setEmail('')
      alert('User already added')
    } else {
      getDirectoryUser(email, 'mail')
    }
  }

  // const handleRoleSelectChange = async (event) => {
  //   const rid = event.target.value
  //   const _role = getRoleById(rid)
  //   if (_role) {
  //     setUser({ ...user, role: { id: _role.id, name: _role.name } })
  //   } else {
  //     setUser({ ...user, role: { id: 0, name: '' } })
  //   }
  //   if (rid === ROLES.BOTTLER.id && !user.mfrProfileIds.length) {
  //     setDirty(false)
  //   } else if (rid === ROLES.BOTTLER_CORP.id && !user.ownershipIds.length) {
  //     setDirty(false)
  //   } else setDirty()
  // }

  const getBreadcrumb = () => {
    return (
      <span><People></People>
        {isNewUser() && (<span>&nbsp;Add&nbsp;New&nbsp;</span>)}
        {!isNewUser() && (<span>&nbsp;Edit&nbsp;</span>)}
        User</span>
    )
  }

  const [manufacturerList, setManufacturerList] = useState([])
  const [ownershipList, setOwnershipList] = useState([])

  const loadManufacturerList = useCallback(async (requestConfig) => {

    const basePath = `${ApiGateway.manufacturer}/search`
    const search = { isActive: true }
    const response = await axios.post(basePath, search, requestConfig)
    if (response.data) {
      const { results } = response.data
      setManufacturerList(results.map(r => { return { label: [
        r.manufacturerId ?? '(####)', r.manufacturerName
      ].join(' - '), value: r.uuid } }))
    }
  }, [])

  const loadOwnershipList = useCallback(async (requestConfig) => {

    // const basePath = `${ApiGateway.ownership}/search`
    // const search = {}
    // const response = await axios.post(basePath, search, requestConfig)
    // if (response.data) {
    //   const { results } = response.data
    const search = searchFilter ?? {
      isActive: true
    }
    const list = await getOwnershipList(search)
    setOwnershipList(list.map(r => { return { label: [r.ownershipId, r.ownershipName].join(' - '), value: r.ownershipId } }))
    // }
  }, [searchFilter])

  const loadGroupMappings = useCallback(async (requestConfig) => {
    if (!requestConfig) requestConfig = await getRequestConfig()

      const path = `${ApiGateway.profile}/roleGroupMappings`
      const response = await axios.get(path, requestConfig)
      return response.data
  }, [])

  const initializePage = useCallback(async () => {
    const requestConfig = await getRequestConfig()

    startLoading()
    const promises = [
      loadManufacturerList(requestConfig),
      loadOwnershipList(requestConfig),
      loadGroupMappings(requestConfig),
      findGroups('NAOU QSE')
    ]

    try {
      const responses = await Promise.allSettled(promises)
      const roleGroupMappings = responses[2]?.status === 'fulfilled' ? (responses[2].value ?? []) : []
      const groupIdentities = responses[3]?.status === 'fulfilled' ? (responses[3].value ?? []) : []
      setGroupConfig({ roleGroupMappings, groupIdentities })

      if (params.koId && params.koId !== 'new' && !initialized) {
        let koIds = []
        if (params.koId === 'selected') {
          koIds = location.state.selected //params.koId.split(',')
        } else {
          koIds = [params.koId]
        }
        const userPromises = koIds.map(async (koId) => {
          const basePath = `${ApiGateway.profile}/${koId}`
          const response = await axios.get(basePath, requestConfig)
          if (!response.data) return null
          const u = response.data
          return {
            ...u,
            koId,
            role: u.roles[0],
            manufacturerUuids: u.manufacturerUuids ?? [],
            mfrProfileIds: u.mfrProfileIds ?? [],
            ownershipIds: u.ownershipIds ?? []
          }
        })
        const userResponses = (await Promise.all(userPromises)).filter(Boolean)
        const _users = [...userResponses]
        handleSetUsers(_users)
      }
    } finally {
      setInitialized(true)
      stopLoading()
    }

  }, [handleSetUsers, initialized, loadGroupMappings, loadManufacturerList, loadOwnershipList, location.state, params.koId, startLoading, stopLoading])

  const getOwnershipOptionLabel = (option) => {
    if (typeof option === 'string' || !option.label) {
      const ownership = ownershipList.find((p) => p.value === (option.value ?? option))
      option = !ownership ? undefined : { value: ownership.value, label: ownership.label }
    }
    return !option ? undefined : option.label
  }

  useEffect(() => {
    initializePage()
  }, [initializePage])

  
  const createAndDownloadCsvFile = async (_data, _name) => {
    let csv
    if(_data) {
      csv = _data
    } else {
      const newData = missingUsers.map(d => ({email: d.email, error: d.errors[0].message}))
      csv = 'Email,Error\n' + newData.map(row => Object.values(row).join(',')).join('\n')
    }
    const a = document.createElement('a')
    a.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv ?? '')}`
    a.download = _name || 'missing-users.csv'
    a.click()
  }

  const csvFileTemplate = '"Email","RoleId","Ownerships","Facilities","Notify"'

  return (<>{ groupConfig && 
        <Page {...props} breadcrumb={getBreadcrumb()} isLoading={isLoading}
          commandBar={<FormViewCommandBar onSave={onSave} onCancel={confirmCancel} enableSave={isDirty && !saveClicked} showClose={false} />}>
          <ConfirmDialog title="Discard all changes?" text="You will not be able to recover your work." open={confirmDialog} hideDialog={hideDialog} handleConfirm={handleClose} yesLabel="Discard" noLabel="Stay on Page" />
          <ConfirmDialog title="Save was not successful" text={getSaveUnsuccessfulText()} open={tryAgainDialog} hideDialog={hideTryAgainDialog} handleConfirm={onSave} yesLabel="Try Again" noLabel="Cancel" />
          <ConfirmDialog title="Errors" text={errorsText} open={errorsDialogOpen} hideDialog={() => setErrorsDialogOpen(false)} handleConfirm={() => setErrorsDialogOpen(false)} yesLabel="OK" />
          <ConfirmDialog title="Some users were not found in the directory" text={"Click DOWNLOAD to download the list of missing users."} open={usersNotFoundDialogOpen} hideDialog={() => setUsersNotFoundDialogOpen(false)} handleConfirm={() => createAndDownloadCsvFile() && setUsersNotFoundDialogOpen(false)} yesLabel="Download" noLabel="Cancel"/>
          <ConfirmDialog title="File error" text={"No users were able to be interpreted from your file. You may have provided a corrupt file or a file of the wrong format. Click DOWNLOAD TEMPLATE to download the example CSV file format."} open={noUsersFoundDialogOpen} hideDialog={() => setNoUsersFoundDialogOpen(false)} handleConfirm={() => createAndDownloadCsvFile(csvFileTemplate, 'BulkUserTemplate.csv') && setNoUsersFoundDialogOpen(false)} noLabel="OK" yesLabel="Download Template"/>
          {isNewUser() && <Paper sx={{ padding: 2, marginBottom: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={12}>
                <TextField label="Email" value={email} fullWidth id="email_input" size='small'
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}><Button onClick={handleLookupClick} variant="outlined" style={{ width: "100%" }}>Add User by Email</Button></Grid>
              {directoryUserNotFound &&
                <ConfirmDialog title="Error" text="An active account was not found for this email address" open={directoryUserNotFound} hideDialog={() => setDirectoryUserNotFound(false)} handleConfirm={() => setDirectoryUserNotFound(false)} yesLabel="OK" />
              }
              {/* style={{height: "40px", lineHeight: "40px"}} */}
              <Grid item xs={4} md={1} style={{ textAlign: "center", lineHeight: "30px" }}>
                <b>OR</b>
              </Grid>
              <Grid item xs={12} md={8}>
                <UploadFileControl
                  startLoading={startLoading}
                  stopLoading={stopLoading}
                  setResults={(results) => {
                    const _results = results.filter(r => !users.find(u => u.email === r.email))
                    const found = []
                    const notFound = []
                    _results.forEach(r => {
                      if (r.errors.filter(f => f.message === 'User not found in directory').length) {
                        notFound.push(r)
                      } else {
                        found.push(r)
                      }
                    })
                    if(notFound.length && found.length) {
                      setMissingUsers(notFound)
                      setUsersNotFoundDialogOpen(true)
                    }
                    if(found.length) {
                      // setIsDirty(true)
                      const _users = [...users, ...found]
                      handleSetUsers(_users)
                    }
                    if(!_results.length) {
                      setNoUsersFoundDialogOpen(true)
                    }
                  }}
                />
              </Grid>
            </Grid>

          </Paper>}
          <div style={{ marginTop: isNewUser() ? 0 : '10px'}}>
          
          {(users || []).length > 0 && users.map((u, i) => {
            return <UserCard
              key={u.email}
              user={u}
              // setDirty={setDirty}
              manufacturerList={manufacturerList}
              ownershipList={ownershipList}
              getOwnershipOptionLabel={getOwnershipOptionLabel}
              onClear={() => onClear(u.email)}
              onDelete={() => onDelete(u)}
              onChange={(user) => {
                const _users = [...users]
                _users[i] = user
                handleSetUsers(_users)
              }}
              editMode={isNewUser() || isMultiEdit()}
              showErrors={(errors) => {
                setErrorsText(errors.join('\n'))
                setErrorsDialogOpen(true)
              }}
              groupConfig={groupConfig}
            />
          })}
          </div>
        </Page>
      }</>)
}